import { envConfigs } from '@config/envConfigs';
import { AxiosResponse, getApiToken, IPagedResultsV1 } from '@gcl/react-core';
import Resumable from 'resumablejs';
import CryptoJS from 'crypto-js';
import { logger } from '@core/Logging/Logger';
import QueryString from 'qs';
import { IResultResponse } from '@common/api.models';
import mime from 'mime-types';
import { apiServerDelete, apiServerGet, apiServerPost } from '@common/axios.services';
import { IUploadChunkProps } from '@file/file.models';
import {
  IProjectFileDownloadRequest,
  IProjectFileProcessResponse,
  IFileResponse,
  IProjectFileDrawingChunkCompleteRequest,
  IProjectFileAttachmentChunkCompleteRequest,
  IProjectFileChunkRequest,
  IProjectFileBase,
  EProjectFileCategory,
  IDeleteProjectFileRequest,
  IProjectFileDiscussionAttachmentChunkCompleteRequest,
  IDownloadTransmittalDrawingsRequest,
  IDownloadDesignOrConstructionDrawingsRequest,
  IDownloadAttachmentsRequest,
  IDownloadDrawingBulkUploadTemplate,
} from './projectFile.models';
import { showToast } from '@core/ApiErrorDisplay';
import { IErrorResponsePair } from '@core/error.models';

export const apiRoutes = {
  drawingDownload: (projectFileId: number) => `/ProjectFiles/${projectFileId}/DownloadDrawing`,
  drawingTempAddChunks: '/ProjectFiles/Drawings/Temp/AddChunks',
  drawingTempAddChunksComplete: '/ProjectFiles/Drawings/Temp/AddChunks/Complete',
  drawingAddChunks: '/ProjectFiles/Drawings/AddChunks',
  drawingAddChunksComplete: '/ProjectFiles/Drawings/AddChunks/Complete',
  downloadTransmittalDrawings: '/ProjectFiles/DownloadTransmittalDrawings',
  downloadDesignDrawings: '/ProjectFiles/DownloadDesignDrawings',
  downloadConstructionDrawings: '/ProjectFiles/DownloadConstructionDrawings',
  attachmentDownload: (projectFileId: number) => `/ProjectFiles/${projectFileId}/DownloadAttachment`,
  attachmentAddChunks: '/ProjectFiles/Attachments/AddChunks',
  attachmentAddChunksComplete: '/ProjectFiles/Attachments/AddChunks/Complete',
  discussionAttachmentAddChunks: '/ProjectFiles/Attachments/Discussions/AddChunks',
  discussionAttachmentAddChunksComplete: '/ProjectFiles/Attachments/Discussions/AddChunks/Complete',
  attachmentTransmittals: '/ProjectFiles/Attachments/Transmittals',
  attachmentMasterSchedules: '/ProjectFiles/Attachments/MasterSchedules',
  attachmentDesignBriefs: '/ProjectFiles/Attachments/DesignBriefs',
  attachmentDesignModifications: '/ProjectFiles/Attachments/DesignModifications',
  attachmentLoadingRemovalCerts: '/ProjectFiles/Attachments/LoadingRemovalCerts',
  attachmentTwcGeRequests: '/ProjectFiles/Attachments/TwcGeRequests',
  projectFiles: '/ProjectFiles',
  downloadAttachments: '/ProjectFiles/DownloadAttachments',
  drawingBulkUploadDownloadTemplate: '/ProjectFiles/Drawings/BulkUpload/DownloadTemplate',
};

export interface IApiTokenProps {
  instance: any;
  scopes: string[];
}

export type IUploadProjectFileRequest<T> = T & {
  file: File;
};

export interface IUploadProjectFileAsyncProps<T> {
  request: IUploadProjectFileRequest<T>;
  onUploadProgress?: (progress: number) => void;
}

export interface IUploadProjectFileAsyncConfigProps {
  apiTokenProps: IApiTokenProps;
  uploadChunkProps?: IUploadChunkProps;
  traceId: string;
}

export const uploadProjectFileDrawingTempAsync = async (
  { request, onUploadProgress }: IUploadProjectFileAsyncProps<IProjectFileDrawingChunkCompleteRequest>,
  { apiTokenProps, uploadChunkProps, traceId }: IUploadProjectFileAsyncConfigProps,
): Promise<IProjectFileProcessResponse> => {
  return uploadProjectFileAsync(
    { request, onUploadProgress },
    { apiTokenProps, uploadChunkProps, traceId },
    apiRoutes.drawingTempAddChunks,
    apiRoutes.drawingTempAddChunksComplete,
  );
};

export const uploadProjectFileDrawingAsync = async (
  { request, onUploadProgress }: IUploadProjectFileAsyncProps<IProjectFileDrawingChunkCompleteRequest>,
  { apiTokenProps, uploadChunkProps, traceId }: IUploadProjectFileAsyncConfigProps,
): Promise<IProjectFileProcessResponse> => {
  return uploadProjectFileAsync(
    { request, onUploadProgress },
    { apiTokenProps, uploadChunkProps, traceId },
    apiRoutes.drawingAddChunks,
    apiRoutes.drawingAddChunksComplete,
  );
};

export const uploadProjectFileAttachmentAsync = async (
  { request, onUploadProgress }: IUploadProjectFileAsyncProps<IProjectFileAttachmentChunkCompleteRequest>,
  { apiTokenProps, uploadChunkProps, traceId }: IUploadProjectFileAsyncConfigProps,
): Promise<IProjectFileProcessResponse> => {
  return uploadProjectFileAsync(
    { request, onUploadProgress },
    { apiTokenProps, uploadChunkProps, traceId },
    apiRoutes.attachmentAddChunks,
    apiRoutes.attachmentAddChunksComplete,
  );
};

export const uploadProjectFileDiscussionAttachmentAsync = async (
  { request, onUploadProgress }: IUploadProjectFileAsyncProps<IProjectFileDiscussionAttachmentChunkCompleteRequest>,
  { apiTokenProps, uploadChunkProps, traceId }: IUploadProjectFileAsyncConfigProps,
): Promise<IProjectFileProcessResponse> => {
  return uploadProjectFileAsync(
    { request, onUploadProgress },
    { apiTokenProps, uploadChunkProps, traceId },
    apiRoutes.discussionAttachmentAddChunks,
    apiRoutes.discussionAttachmentAddChunksComplete,
  );
};

export const uploadProjectFileAsync = async <T extends IProjectFileChunkRequest>(
  { request, onUploadProgress }: IUploadProjectFileAsyncProps<T>,
  { apiTokenProps, uploadChunkProps, traceId }: IUploadProjectFileAsyncConfigProps,
  targetRouteUpload: string,
  targetRouteFileSuccess: string,
): Promise<IProjectFileProcessResponse> => {
  const token = await getApiToken(apiTokenProps.instance, apiTokenProps.scopes);

  const { projectId, file, ...chunkRequest } = request;
  const maxFileSize = 150 * 1024 * 1024; // 150MB

  const totalFileSize = file.size;
  logger.info(`Total file size: ${totalFileSize} bytes`);

  if (totalFileSize > maxFileSize) {
    const error: IErrorResponsePair = {
      error: 'PDF exceeds the maximum allowed size of 150MB.',
      genericError: 'The uploaded file exceeds the maximum allowed size of 150MB.',
    };
    showToast(error, traceId);
    return Promise.reject(error);
  }

  const chunkSize = uploadChunkProps?.chunkSize || 5;
  const forceChunkSize = uploadChunkProps?.forceChunkSize ?? true;

  const localChunkRequest = {} as any;
  Object.keys(chunkRequest).forEach((key) => {
    if ((chunkRequest as any)[key]) localChunkRequest[key] = (chunkRequest as any)[key];
  });

  delete localChunkRequest.url;

  const target = `${envConfigs.apiBaseUrl}${targetRouteUpload}`;
  try {
    const uploadFilePromise = new Promise<AxiosResponse<IProjectFileProcessResponse>>((resolve, reject) => {
      const r = new Resumable({
        headers: {
          'X-Traceid': traceId,
          Authorization: `Bearer ${token}`,
          ContentType: file.type,
        },
        target,
        testChunks: false,
        chunkSize: (chunkSize || 25) * 1024 * 1024,
        forceChunkSize,
        simultaneousUploads: uploadChunkProps?.simultaneousUploads || 5,
        maxChunkRetries: 5,
        chunkRetryInterval: 10000, // 10 seconds
        chunkNumberParameterName: 'chunkNumber',
        totalChunksParameterName: 'totalChunks',
        chunkSizeParameterName: 'chunkSize',
        totalSizeParameterName: 'totalSize',
        identifierParameterName: 'identifier',
        fileNameParameterName: 'fileName',
        relativePathParameterName: 'relativePath',
        currentChunkSizeParameterName: 'currentChunkSize',
        typeParameterName: 'type',
        query: {
          ...localChunkRequest,
          projectId,
        },
      });

      r.addFile(file);
      r.on('fileAdded', async (f, event) => {
        r.upload();
      });
      r.on('fileSuccess', async () => {
        const md5 = await getFileMD5(file, (progress) => {
          if (onUploadProgress) onUploadProgress(Math.round(progress * 100 * 100) / 100 / 2 + 50);
        });

        const params = QueryString.stringify(
          {
            md5ContentHash: md5,
            totalChunks: r.files[0].chunks.length,
            fileContentType: !!file.type && file.type.length > 0 ? file.type : mime.lookup(file.name),
            ...localChunkRequest,
            projectId,
          },
          { encode: true, allowDots: true },
        );

        try {
          const result = await apiServerPost<IProjectFileProcessResponse>(
            `${envConfigs.apiBaseUrl}${targetRouteFileSuccess}?${params}`,
            null,
          );
          resolve(result);
        } catch (ex) {
          reject(ex);
        }
      });
      r.on('fileError', (f, message) => {
        logger.error({ message: 'Resumable upload file: on file error', extras: { message } });
        try {
          const { status, ...data } = JSON.parse(message);
          const axiosError = { response: { status, data: { ...data, traceId } } };
          reject(axiosError);
        } catch (error) {
          const axiosError = { response: { data: { traceId } } };
          reject(axiosError);
        }
      });
      r.on('fileProgress', () => {
        if (onUploadProgress) onUploadProgress(Math.round(r.progress() * 100 * 100) / 100 / 2);
      });
    });

    const uploadFileResult = await uploadFilePromise;
    return uploadFileResult.data;
  } catch (ex: any) {
    if (((ex?.response?.config?.url ?? '') as string).includes(targetRouteFileSuccess)) {
      throw ex;
    }
    // handleFileRelatedError(chunkRequest.fileName, 'upload', 'uploadFileAsync', ex);
    throw ex;
  }
};

export const downloadProjectFileAsync = async (
  downloadFileRequest: IProjectFileDownloadRequest,
): Promise<IFileResponse> => {
  const { onDownloadProgressUpdate, type, ...request } = downloadFileRequest;
  const apiRoute =
    type === EProjectFileCategory.attachments
      ? apiRoutes.attachmentDownload(request.projectFileId)
      : apiRoutes.drawingDownload(request.projectFileId);

  const fileResponse = await apiServerPost<Blob>(`${envConfigs.apiBaseUrl}${apiRoute}`, request, {
    responseType: 'blob',
    onDownloadProgress: (progressEvent) => {
      if (onDownloadProgressUpdate) {
        const { total, loaded } = progressEvent;

        const percentCompleted = Math.floor((loaded / total) * 100);
        onDownloadProgressUpdate(percentCompleted);
      }
    },
  });

  let filename = '';
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(fileResponse.headers['content-disposition']);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, '');
  }
  return { file: fileResponse.data, filename };
};

export const downloadTransmittalDrawingsAsync = async (
  downloadTransmittalDrawingsRequest: IDownloadTransmittalDrawingsRequest,
): Promise<IFileResponse> => {
  const { onDownloadProgressUpdate, ...request } = downloadTransmittalDrawingsRequest;
  const fileResponse = await apiServerPost<Blob>(
    `${envConfigs.apiBaseUrl}${apiRoutes.downloadTransmittalDrawings}`,
    request,
    {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (onDownloadProgressUpdate) {
          const { total, loaded } = progressEvent;

          const percentCompleted = Math.floor((loaded / total) * 100);
          onDownloadProgressUpdate(percentCompleted);
        }
      },
    },
  );

  let filename = '';
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(fileResponse.headers['content-disposition']);
  const responseContentType = fileResponse.headers['content-type'];
  const ext = mime.extension(responseContentType);
  if (matches != null && matches[1]) {
    filename = `${matches[1].replace(/['"]/g, '')}.${ext}`;
  }
  return { file: fileResponse.data, filename };
};

export const downloadDesignOrConstructionDrawingsAsync = async (
  downloadDesignOrConstructionDrawingsRequest: IDownloadDesignOrConstructionDrawingsRequest,
): Promise<IFileResponse> => {
  const { onDownloadProgressUpdate, ...request } = downloadDesignOrConstructionDrawingsRequest;
  const fileResponse = await apiServerPost<Blob>(
    `${envConfigs.apiBaseUrl}${
      downloadDesignOrConstructionDrawingsRequest.isConstructionDrawing
        ? apiRoutes.downloadConstructionDrawings
        : apiRoutes.downloadDesignDrawings
    }`,
    request,
    {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (onDownloadProgressUpdate) {
          const { total, loaded } = progressEvent;

          const percentCompleted = Math.floor((loaded / total) * 100);
          onDownloadProgressUpdate(percentCompleted);
        }
      },
    },
  );

  let filename = '';
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(fileResponse.headers['content-disposition']);
  const responseContentType = fileResponse.headers['content-type'];
  const ext = mime.extension(responseContentType);
  if (matches != null && matches[1]) {
    filename = `${matches[1].replace(/['"]/g, '')}.${ext}`;
  }
  return { file: fileResponse.data, filename };
};

export const blobToBase64 = async (blob: Blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const downloadFile = async (blob: Blob, fileName: string): Promise<void> => {
  if (!blob) return;
  const arrayBuffer = await blob.arrayBuffer();
  const downloadElement = document.createElement('a');
  downloadElement.style.display = 'none';
  downloadElement.href = window.URL.createObjectURL(new Blob([arrayBuffer]));
  downloadElement.download = fileName;
  document.body.appendChild(downloadElement);
  downloadElement.click();
  window.URL.revokeObjectURL(downloadElement.href);
};

const readFileAsChunked = (
  file: Blob,
  chunkCallback: (readerResult: string, offset: number, fileSize: number) => void,
  endCallback: (error: any) => void,
  readNextCallBack?: (fileReader: FileReader, fileSlice: Blob) => void,
) => {
  const fileSize = file.size;
  const chunkSize = 4 * 1024 * 1024; // 4MB
  let offset = 0;

  const reader = new FileReader();
  reader.onload = () => {
    if (reader.error) {
      endCallback(reader.error || {});
      return;
    }
    offset += (reader.result as string).length;
    // callback for handling read chunk
    chunkCallback(reader.result as string, offset, fileSize);
    if (offset >= fileSize) {
      endCallback(null);
      return;
    }
    readNext();
  };

  reader.onerror = (err) => {
    endCallback(err || {});
  };

  const readNext = () => {
    const fileSlice = file.slice(offset, offset + chunkSize);
    if (!readNextCallBack) reader.readAsBinaryString(fileSlice);
    else readNextCallBack(reader, fileSlice);
  };
  readNext();
};

export const getFileMD5 = (file: File, cbProgress?: (int: number) => void): Promise<string> => {
  return new Promise((resolve, reject) => {
    const md5 = CryptoJS.algo.MD5.create();
    readFileAsChunked(
      file,
      (chunk, offs, total) => {
        md5.update(CryptoJS.enc.Latin1.parse(chunk));
        if (cbProgress) {
          cbProgress(offs / total);
        }
      },
      (err) => {
        if (err) {
          reject(err);
        } else {
          const hash = md5.finalize();
          const hashHex = hash.toString(CryptoJS.enc.Hex);
          resolve(hashHex);
        }
      },
    );
  });
};

export const getAttachmentAsync = async <T>(apiRoute: string, params: T) => {
  const result = await apiServerGet<IPagedResultsV1<IProjectFileBase>>(`${envConfigs.apiBaseUrl}${apiRoute}`, {
    params,
  });
  return result.data;
};

export const deleteProjectFileAsync = async (request: IDeleteProjectFileRequest) => {
  const { id, ...params } = request;
  const result = await apiServerDelete<IResultResponse>(`${envConfigs.apiBaseUrl}${apiRoutes.projectFiles}/${id}`, {
    params,
  });
  return result.data;
};

export const downloadAttachmentsAsync = async (
  downloadAttachmentsRequest: IDownloadAttachmentsRequest,
): Promise<IFileResponse> => {
  const { onDownloadProgressUpdate, ...request } = downloadAttachmentsRequest;
  const fileResponse = await apiServerPost<Blob>(`${envConfigs.apiBaseUrl}${apiRoutes.downloadAttachments}`, request, {
    responseType: 'blob',
    onDownloadProgress: (progressEvent) => {
      if (onDownloadProgressUpdate) {
        const { total, loaded } = progressEvent;

        const percentCompleted = Math.floor((loaded / total) * 100);
        onDownloadProgressUpdate(percentCompleted);
      }
    },
  });
  return convertFileResponseToFile(fileResponse);
};

const convertFileResponseToFile = (fileResponse: AxiosResponse<Blob, any>) => {
  let filename = '';
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(fileResponse.headers['content-disposition']);
  const responseContentType = fileResponse.headers['content-type'];
  const ext = mime.extension(responseContentType);
  if (matches != null && matches[1]) {
    filename = `${matches[1].replace(/['"]/g, '')}.${ext}`;
  }
  return { file: fileResponse.data, filename };
};

export const downloadDrawingBulkUploadTemplateAsync = async (
  downloadTemplateRequest: IDownloadDrawingBulkUploadTemplate,
): Promise<IFileResponse> => {
  const { onDownloadProgressUpdate, ...request } = downloadTemplateRequest;
  const fileResponse = await apiServerPost<Blob>(
    `${envConfigs.apiBaseUrl}${apiRoutes.drawingBulkUploadDownloadTemplate}`,
    null,
    {
      params: request,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (onDownloadProgressUpdate) {
          const { total, loaded } = progressEvent;

          const percentCompleted = Math.floor((loaded / total) * 100);
          onDownloadProgressUpdate(percentCompleted);
        }
      },
    },
  );
  return convertFileResponseToFile(fileResponse);
};
